<template>
	<div class="editable-wrapper" :class="{ 'editable-area': editable, animate: animate }" @click="handleClick" ref="editableContainer">
		<transition name="editor-pop" appear>
			<div v-show="isEditing && !applyClick" class="editor-container">
				<div class="editing-label">{{ editLabel }}</div>
				<div class="inline-editor-wrapper" :style="editorStyles">
					<textarea
						ref="editInput"
						v-model="editedValue"
						@keydown.esc="cancelEdit"
						@keydown.enter="handleEnter"
						@blur="saveEdit"
						class="inline-editor__input"
						@click.stop
						:maxlength="maxlength"
					/>
				</div>
			</div>
		</transition>

		<slot></slot>
		<div class="popup-backdrop" ref="modalBackdrop" v-if="isEditing" @click="saveEdit"></div>
	</div>
</template>

<script>
	export default {
		name: 'EditableComponent',
		props: {
			editable: {
				type: Boolean,
				default: false,
			},
			value: {
				type: String,
				default: '',
			},
			editLabel: {
				type: String,
				default: 'Edit',
			},
			singleLine: {
				type: Boolean,
				default: false,
			},
			maxlength: {
				type: [Number, String],
			},
			applyClick: {
				type: Boolean,
				default: false,
			},
			transparent: {
				type: Boolean,
				default: false,
			},
			animate: {
				type: Boolean,
				default: false,
			},
		},
		data() {
			return {
				isEditing: false,
				editedValue: this.value,
				editorStyles: {
					width: 'auto',
					height: 'auto',
				},
			}
		},

		watch: {
			value(newValue) {
				this.editedValue = newValue
			},
		},
		methods: {
			addPx(original, added) {
				const originalPxNum = parseInt(original.split('px')[0])
				return `${originalPxNum + added}px`
			},
			handleClick(event) {
				if (!this.editable || this.isEditing) return

				this.$emit('opened')

				event.preventDefault()
				event.stopPropagation()

				if (this.applyClick) {
					this.$emit('editClick')
					return
				}

				if (this.$refs.editableContainer) {
					const container = this.$refs.editableContainer
					const styles = window.getComputedStyle(container)

					this.editorStyles = {
						width: this.addPx(styles.width, 4),
						height: this.addPx(styles.height, 12),
						fontSize: styles.fontSize,
						lineHeight: styles.lineHeight,
					}
				}

				this.editedValue = this.value
				this.isEditing = true

				// Focus the input after it's been rendered
				this.$nextTick(() => {
					if (this.$refs.editInput) {
						this.$refs.editInput.focus()
						this.$refs.editInput.select()
						setTimeout(() => {
							this.$refs.editInput.scrollIntoView({
								behavior: 'smooth',
								block: 'center',
							})
						}, 200)
					}
				})
			},
			handleEnter(event) {
				if (event.shiftKey) {
					return
				}
				event.preventDefault()
				this.saveEdit()
			},
			saveEdit(e) {
				const trimmedVal = this.editedValue.trim()
				if (trimmedVal !== '') {
					this.$emit('input', trimmedVal)
					this.$emit('change', trimmedVal)
				}
				e?.stopPropagation()
				this.isEditing = false
			},
			cancelEdit() {
				this.editedValue = this.value
				this.isEditing = false
			},
		},
	}
</script>

<style scoped lang="scss">
	.editable-wrapper {
		position: relative;
		width: 100%;
		min-height: 1em;
		height: 100%;
		overflow: visible;
		display: flex;
	}

	.editable-area {
		cursor: pointer;
		z-index: 1;
	}

	.editing-label {
		position: absolute;
		top: -22px;
		left: 2px;
		background-color: var(--stan-primary-primary-color);
		color: white;
		font-size: 12px;
		padding: 2px 6px;
		border-radius: 4px;
		pointer-events: none;
		z-index: 12;
		font-weight: 500;
		line-height: 16px;
		font-family: 'Inter';
	}
	.editable-area:hover .label-overlay {
		display: block;
	}

	.editor-container {
		position: absolute;
		top: -6px;
		left: -3px;
		width: 100%;
		height: 100%;
		z-index: 10;
	}

	.inline-editor-wrapper {
		position: relative;
		z-index: 11;
		width: 100%;
		height: 100%;
	}

	.inline-editor__input {
		width: 100%;
		height: 100%;
		overflow: hidden;
		white-space: pre-wrap;
		word-wrap: break-word;
		box-sizing: border-box;
		resize: none;
		border-radius: 8px;
		padding: 3px 6px;
		border: 2px solid var(--stan-primary-primary-color);
		background: white;
		filter: drop-shadow(-5px 5px 20px#00000060);
		font-size: inherit;
		font-family: inherit;
		color: var(--stan-text-dark-color);
		&:focus-visible {
			outline: none;
		}
	}

	.edit-label {
		position: absolute;
		top: -24px;
		left: 0;
		background-color: var(--stan-primary-primary-color);
		color: white;
		padding: 2px 6px;
		font-size: 10px;
		border-radius: 3px 3px 0 0;
		z-index: 11;
		pointer-events: none;
	}

	.editor-pop-enter-active,
	.editor-pop-leave-active {
		transition: opacity 0.3s ease, transform 0.3s ease;
	}

	.editor-pop-enter,
	.editor-pop-leave-to {
		opacity: 0;
		transform: scale(0.98);
		transform-origin: top left;
	}
	.popup-backdrop {
		position: fixed;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		background: rgba(0, 0, 0, 0.5);
		opacity: 0;
		transition: opacity 0.3s ease;
		z-index: 9;
	}
	@keyframes pulse {
		0%,
		100% {
			transform: scale(1);
		}
		50% {
			transform: scale(1.03);
		}
	}

	.animate {
		animation: pulse 1.2s ease-in-out infinite;
		transform-origin: center;
	}
</style>
